import { ChevronDown, ChevronRight, Settings2 } from "lucide-react";
import TemplateAdvancedSettings from "./TemplateAdvancedSettings";
import { User } from "@supabase/supabase-js";
import { TemplatePermissions } from "../types";

interface AdvancedSettingsToggleProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: string;
  onPromptChange: (value: string, options?: { markPromptTouched?: boolean }) => void;
  isMobile: boolean;
  user?: User | null;
  isPromptTouched: boolean;
  isAiInstructionDirty?: boolean;
  permissions?: TemplatePermissions;
  forceUpdateKey?: string | number;
}

const AdvancedSettingsToggle = ({
  isOpen,
  onOpenChange,
  prompt,
  onPromptChange,
  isMobile,
  user,
  isPromptTouched,
  isAiInstructionDirty = false,
  permissions = { canView: true, canEdit: true, canDelete: true, canCopy: true, canTest: true },
  forceUpdateKey,
}: AdvancedSettingsToggleProps) => {
  const handleToggle = () => onOpenChange(!isOpen);
  const trimmedPrompt = prompt.trim();
  const previewText = trimmedPrompt
    ? `${trimmedPrompt.slice(0, 160)}${trimmedPrompt.length > 160 ? "…" : ""}`
    : "Промпт генерируется автоматически из описания и примера";

  return (
    <div className="border border-border/60 rounded-lg bg-card/60 shadow-sm">
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={handleToggle}
            className="flex items-center text-sm font-medium text-foreground hover:text-muted-foreground"
          >
            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span className="ml-1">Дополнительные настройки</span>
          </button>
        </div>
      </div>

      {!isOpen && (
        <div className="px-4 pb-4 text-sm text-muted-foreground">
          Инструкции для ИИ определяют правила формирования итогового документа. Инструкции для ИИ создаются автоматически на основе описания и примера документа, но также доступны для ручного редактирования.
        </div>
      )}

      {isOpen && (
        <div className="border-t border-border/60 p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {permissions.canEdit
              ? `Отредактируйте инструкции для ИИ вручную, если требуется точное поведение. Текущий промпт ${isPromptTouched ? "редактировался пользователем" : "создан автоматически"}.`
              : "Инструкции для ИИ, которые используются для обработки ваших записей."
            }
          </p>
          {permissions.canEdit && isAiInstructionDirty && (
            <div className="rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-700">
              Инструкции для ИИ были изменены. Не забудьте сохранить шаблон, чтобы применить изменения.
            </div>
          )}
            <TemplateAdvancedSettings
            prompt={prompt}
            setPrompt={(value) => onPromptChange(value, { markPromptTouched: true })}
            isMobile={isMobile}
            user={user}
            canEdit={permissions.canEdit}
            forceUpdateKey={forceUpdateKey}
          />
          {!permissions.canEdit && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
              Это системный шаблон. Инструкции доступны только для просмотра.
            </div>
          )}
          {permissions.canEdit && !isPromptTouched && (
            <div className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              Пока вы не вносите изменений, промпт продолжит подстраиваться под описание и пример документа.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSettingsToggle;
